import {FpAxios} from '@/services/FpAxios';
import LoanApplication, {LoanApplicationDTO} from '@/models/LoanApplication';
import {CreditDetails} from '@/modules/multiForm/shared/MultiformModels';
import BankContact, {BankContactDTO} from '@/models/BankContact';
import {HistoryElement, HistoryElementDTO} from '@/models/HistoryElement';
import Note from '@/models/Note';
import {LoanApplicationCategory} from '@/modules/activities/enums/LoanApplicationCategory';
import { FiltersResponse, LoanApplicationFilter} from '@/models/Filter';
import {ApplicationDocumentsDTO} from '@/models/ChecklistDocuments';
import processProxy from '@/env/processProxy';
import {
  LoanApplicationsStepChangeBody,
  LoanApplicationsStepChangeResult
} from '@/models/LoanApplicationsStepChange';

// eslint-disable-next-line vue/max-len
class LoanApplicationsApi {
  private loanApplicationsAxios = FpAxios.create();
  readonly baseUrl = 'applications';

  constructor() {
    this.loanApplicationsAxios.interceptors.response.use((response: any) => response, (error: any) => {
      return Promise.reject(error);
    });
  }

  mapCreditDetails(creditDetails: CreditDetails): CreditDetails {
    if (!creditDetails.inTranche) {
      creditDetails.tranches.forEach((tranche, index: number) => {
        creditDetails.tranches[index].bankAccount = creditDetails.withdrawCreditAccountNumber;
        creditDetails.tranches[index].trancheDate = creditDetails.withdrawCreditDate;
        creditDetails.tranches[index].bankAccountOwner = creditDetails.withdrawCreditAccountOwnerName;
      });
    }
    return creditDetails;
  }

  async editCreditDetails(id: number, creditDetails: CreditDetails) {
    creditDetails = this.mapCreditDetails(creditDetails);
    const response = await this.loanApplicationsAxios.put(`${this.baseUrl}/${id}/credit-details`, creditDetails);
    return response.data;
  }

  async getLoanApplication(id: number): Promise<LoanApplication> {
    const response = await this.loanApplicationsAxios.get<LoanApplicationDTO>(`${this.baseUrl}/${String(id)}`);
    return new LoanApplication(response.data);
  }

  async getLoanApplicationList(): Promise<Array<LoanApplication>> {
    const response = await this.loanApplicationsAxios.get<Array<LoanApplicationDTO>>(`${this.baseUrl}/list`);
    return response.data.map((LoanApplicationDTO: LoanApplicationDTO) => new LoanApplication(LoanApplicationDTO));
  }

  async setLoanApplicationStep(loanApplicationId: number, stepId: number): Promise<LoanApplication> {
    const response = await this.loanApplicationsAxios.put<LoanApplication>(`${this.baseUrl}/${loanApplicationId}/step/${stepId}`);
    return response.data;
  }

  async setLoanApplicationsStep(loanApplicationIds: Array<number>, stepId: number): Promise<LoanApplicationsStepChangeResult> {
    const data = {status: stepId, applications: loanApplicationIds, };
    const response = await this.loanApplicationsAxios.put<LoanApplicationsStepChangeBody>(`${this.baseUrl}/status`, data);
    return new LoanApplicationsStepChangeResult(loanApplicationIds.length, response.data);
  }

  async removeLoanApplication(loanApplicationId: number): Promise<LoanApplication> {
    const response = await this.loanApplicationsAxios.delete(`${this.baseUrl}/${loanApplicationId}`);
    return response.data;
  }

  async removeLoanApplications(loanApplicationIds: Array<number>): Promise<void> {
    await this.loanApplicationsAxios.delete(this.baseUrl, {data: loanApplicationIds, });
  }

  public async getDocumentsForLoanApplication(id: number): Promise<ApplicationDocumentsDTO> {
    const response = await this.loanApplicationsAxios.get<ApplicationDocumentsDTO>(`${this.baseUrl}/${id}/documents`);
    return response.data;
  }

  public async updatePreDecision(application: LoanApplication): Promise<void> {
    await this.loanApplicationsAxios.put<LoanApplication>(`${this.baseUrl}/${application.id}/pre-decision/${application.preDecision}`);
  }

  public async downloadAllZippedFiles(application: LoanApplication, onProgress: (progress: ProgressEvent) => void) {
    const query = `?merged_general=${application.categoryId === LoanApplicationCategory.CASH}`;
    const response = await this.loanApplicationsAxios.get(`${this.baseUrl}/${application.id}/zipped-filled-documents${query}`, {
      responseType: 'blob',
      onDownloadProgress: onProgress,
    });
    return response.data;
  }

  public getNestIframeUrl(applicationId: number): string {
    return `${processProxy.VUE_APP_SIMULATION_URL}applications/${applicationId}#/dane-klienta`;
  }

  public async updateProcessType(application: LoanApplication): Promise<void> {
    await this.loanApplicationsAxios.put<LoanApplication>(`${this.baseUrl}/${application.id}/process-type/${application.processType}`);
  }

  public async sendToVerification(application: LoanApplication): Promise<LoanApplication> {
    const data = {
      bankAgentEmail: application.bankAgentEmail,
      bankProvisionReplacement: application.bankProvisionReplacement,
      bankBranchId: application.bankBranchId,
    };
    const response = await this.loanApplicationsAxios.put(`${this.baseUrl}/${application.id!}/send-to-verification`, data);
    return new LoanApplication(response.data);
  }

  public async accept(application: LoanApplication, emailContent: string): Promise<void> {
    const data = {
      bankAgentEmail: application.bankAgentEmail,
      bankProvisionReplacement: application.bankProvisionReplacement,
      emailContent: emailContent,
    };
    await this.loanApplicationsAxios.put(`${this.baseUrl}/${application.id!}/accept`, data);
  }

  public async sendDirectlyToBank(application: LoanApplication, emailContent: string): Promise<void> {
    const data = {
      bankAgentEmail: application.bankAgentEmail,
      bankProvisionReplacement: application.bankProvisionReplacement,
      bankBranchId: application.bankBranchId,
      emailContent: emailContent,
    };
    await this.loanApplicationsAxios.put(`${this.baseUrl}/${application.id!}/send-directly-to-bank`, data);
  }

  public async reject(application: LoanApplication): Promise<void> {
    await this.loanApplicationsAxios.put(`${this.baseUrl}/${application.id!}/reject`);
  }

  public async returnForCorrection(application: LoanApplication): Promise<void> {
    await this.loanApplicationsAxios.put(`${this.baseUrl}/${application.id!}/return-for-correction`);
  }

  public async getBanksContacts(applicationId: number): Promise<BankContact[]> {
    const response = await this.loanApplicationsAxios.get<{contacts: Array<BankContactDTO>}>(`${this.baseUrl}/${applicationId}/bank-contacts`);
    return response.data.contacts.map((contact: BankContactDTO) => new BankContact(contact));
  }

  public async addBanksContact(applicationId: number, newBankContact: BankContact): Promise<BankContact> {
    const response = await this.loanApplicationsAxios.post(`${this.baseUrl}/${applicationId}/bank-contacts`, newBankContact);
    return response.data;
  }

  public async getApplicationHistory(applicationId: number): Promise<Array<HistoryElement<LoanApplication>>> {
    const response = await this.loanApplicationsAxios.get(`${this.baseUrl}/${applicationId}/history`);
    return response.data.map((historyElementDTO: HistoryElementDTO<any>) => new HistoryElement(historyElementDTO));
  }

  public async addNote(applicationId: number, note: {text: string}): Promise<Note> {
    const response = await this.loanApplicationsAxios.post(`${this.baseUrl}/${applicationId}/notes`, note);
    return new Note(response.data);
  }

  public async setVerifier(application: LoanApplication) {
    const response = await this.loanApplicationsAxios.put(`${this.baseUrl}/${application.id}/verifier`);
    return response.data;
  }

  public async unsetVerifier(application: LoanApplication) {
    const response = await this.loanApplicationsAxios.delete(`${this.baseUrl}/${application.id}/verifier`);
    return response.data;
  }

  public async filterLoanApplications(filters?: LoanApplicationFilter): Promise<FiltersResponse<LoanApplication>> {
    const response = await this.loanApplicationsAxios.post<FiltersResponse<LoanApplication>>(`${this.baseUrl}/search`, filters ? filters.apiData : {});
    response.data.items = response.data.items
      .map((loanApplicationDTO: LoanApplicationDTO) => new LoanApplication(loanApplicationDTO));
    return response.data;
  }
}

export default new LoanApplicationsApi();
